import { Injectable } from "@angular/core";
import { AIServices } from "../ai-services";
import { AnalysisNode, nextSteps } from "../../common/analysis-model";
import { GraphData, NODE_SUPERTYPE, ORGANISATION_MODEL_KEY } from "../../common/linqset-model";
import { AIModelChange, ModelChangeAnswer, ModelChangeContext, ModelChangeResponse } from "../../common/model-changes";
import { createInsightsFilter, DEFAULT_CURRENCY, getModelsForFilter, Organisation, Project } from "../../common/organisation";
import { MODEL_API, ORGANISATION_API, RepositoryService } from "../../linq-utils/repository.service";
import { InsightsService } from "../../model-services/insights.service";
import { EpmAppliedChange, EpmFutureStatePlan, graphForWorkspace, modelsForChanges, PlannedChange, planEpmFutureState } from "../../epm/epm-future-state";

const COMPONENT = "AI model changes"

// the project as AI is told about it, with what's needed to make the changes it gives back
export interface PreparedProject {
    context:ModelChangeContext
    models:{ _key:string, name:string }[]
    usage:Map<string, Set<string>>          // the keys of the models each role and system is used in, by its key
}

@Injectable({ providedIn: 'root' })
export class AIModelChangeService {
    constructor(private aiServices:AIServices, private insightsService:InsightsService, private repoService:RepositoryService) {}

    // reads the project's roles, systems and actions, for AI to work out what a description refers to
    async prepare(organisation:Organisation, project:Project):Promise<PreparedProject> {
        const filter = createInsightsFilter(project._key)
        const [analysis, organisationModel] = await Promise.all([
            this.insightsService.loadAnalysisGraphForFilter(organisation, filter),
            this.loadGraph(organisation, project, ORGANISATION_MODEL_KEY),
        ])
        const models = getModelsForFilter(filter, organisation, project).map(m => ({ _key: m._key, name: m.name }))
        const modelName = new Map(models.map(m => [m._key, m.name]))
        const nodeName = new Map(organisationModel.nodes.map(n => [n._key, n.name]))

        // which models each role and system is used in: performing or automating actions, or used by the roles that do
        const usage = new Map<string, Set<string>>()
        const use = (key:string, modelKey:string) => (usage.get(key) || usage.set(key, new Set()).get(key)!).add(modelKey)
        const actions = (analysis.nodes as AnalysisNode[]).filter(n => n.superType === NODE_SUPERTYPE.ACTION && modelName.has(n.modelKey))
        for (const a of actions) {
            for (const r of a.roles || []) {
                use(r._key, a.modelKey)
                ;(r.systemKeys || []).forEach(s => use(s, a.modelKey))
            }
            ;(a.systems || []).forEach(s => use(s, a.modelKey))
        }
        const usedIn = (key:string) => [...(usage.get(key) || [])].map(k => modelName.get(k)!).filter(n => !!n)
        // where each action's items go next, so AI can restructure the flow
        const actionNames = new Map(actions.map(a => [a._key, a.name]))

        const context:ModelChangeContext = {
            project: project.name,
            currency: organisation.currencyCode || DEFAULT_CURRENCY,
            models: models.map(m => m.name),
            roles: organisationModel.nodes.filter(n => n.superType === NODE_SUPERTYPE.ROLE)
                .map(n => ({ name: n.name, people: n.properties?.people || 0, annualCost: n.properties?.annualCost || 0, models: usedIn(n._key) })),
            systems: organisationModel.nodes.filter(n => n.superType === NODE_SUPERTYPE.SYSTEM)
                .map(n => ({ name: n.name, annualCost: n.properties?.fixedCost || 0, models: usedIn(n._key) })),
            actions: actions.map(a => ({
                name: a.name,
                model: modelName.get(a.modelKey)!,
                roles: (a.roles || []).map(r => nodeName.get(r._key)).filter((n):n is string => !!n),
                systems: (a.systems || []).map(s => nodeName.get(s)).filter((n):n is string => !!n),
                hours: Math.round(a.hours || 0),
                minutes: Math.round((a.durationSeconds || 0) / 6) / 10,
                next: nextSteps(a, analysis.edges, actionNames),
            })),
        }
        return { context, models, usage }
    }

    async ask(organisation:Organisation, project:Project, prepared:PreparedProject, description:string, answers:ModelChangeAnswer[]):Promise<ModelChangeResponse> {
        return this.aiServices.generateModelChanges(organisation._key, project._key, { description, answers, context: prepared.context })
    }

    // makes the changes in the project, and works out its insights again. Returns what was made, and what couldn't be
    async apply(organisation:Organisation, project:Project, prepared:PreparedProject, changes:PlannedChange[]):Promise<EpmAppliedChange[]> {
        const plan = await this.plan(organisation, project, prepared, changes)
        await this.save(organisation, project, plan)
        return plan.applied
    }

    // works out the changes to each model from the project as it is now, without saving them: what would be made, and
    // what couldn't be
    async plan(organisation:Organisation, project:Project, prepared:PreparedProject, changes:PlannedChange[]):Promise<EpmFutureStatePlan> {
        const organisationModel = await this.loadGraph(organisation, project, ORGANISATION_MODEL_KEY)

        // the models changed actions are in, and those any replaced role or system is used in
        const replaced = changes.filter(c => c.kind === 'replaceRole' || c.kind === 'replaceSystem').map(c => {
            const superType = c.kind === 'replaceRole' ? NODE_SUPERTYPE.ROLE : NODE_SUPERTYPE.SYSTEM
            const name = (c.kind === 'replaceRole' ? c.role : c.system)?.trim().toLowerCase()
            return organisationModel.nodes.find(n => n.superType === superType && n.name.trim().toLowerCase() === name)?._key
        })
        const modelKeys = [...new Set([...modelsForChanges(changes, prepared.models), ...replaced.flatMap(k => k ? [...(prepared.usage.get(k) || [])] : [])])]
        const graphs = await Promise.all(modelKeys.map(key => this.loadGraph(organisation, project, key)))

        return planEpmFutureState(changes, {
            organisationKey: organisation._key,
            projectKey: project._key,
            models: prepared.models,
            organisationModel,
            graphs: Object.fromEntries(modelKeys.map((key, i) => [key, graphs[i]])),
        })
    }

    // saves a plan's changes to the project, and works out its insights again
    async save(organisation:Organisation, project:Project, plan:EpmFutureStatePlan) {
        // roles and systems first, as the models' changes can use the ones it adds
        const order = Object.keys(plan.changes).sort((a, b) => (a === ORGANISATION_MODEL_KEY ? -1 : b === ORGANISATION_MODEL_KEY ? 1 : 0))
        for (const key of order) {
            await this.repoService.callSingleAPI(this.repoService.update(MODEL_API(organisation._key, key, project._key), plan.changes[key]), COMPONENT, "Save the changes")
        }
        if (order.length) {
            await this.repoService.callSingleAPI(this.repoService.update(`${ORGANISATION_API(organisation._key, project._key)}/refresh`, {}), COMPONENT, "Refresh the project's insights")
        }
    }

    private async loadGraph(organisation:Organisation, project:Project, modelKey:string):Promise<GraphData> {
        const graph = await this.repoService.callSingleAPI(this.repoService.getData<GraphData>(MODEL_API(organisation._key, modelKey, project._key)), COMPONENT, "Load the project's model")
        return modelKey === ORGANISATION_MODEL_KEY ? graph : graphForWorkspace(graph, organisation, project)
    }
}
